// The orders your house's heroes carry (`heroes.quests`).
//
// Read off the snapshot; the sim decides whether an order stands, and pays
// the hero out when the objective is met. The tab only shows what the sim
// says.

import { type HeroQuest, type HeroQuests, activeQuests } from '../../heroes/quests.js';
import { useT } from '../../i18n/index.js';

export interface HeroQuestSectionProps {
  /** The orders of the local house, while `heroes.quests` is on. */
  readonly heroQuests?: HeroQuests | null;
}

export function HeroQuestSection({ heroQuests }: HeroQuestSectionProps) {
  const t = useT();
  // The tab lists the orders that are still open. A finished or failed
  // order is shown on the hero's own panel, which is where it happened.
  const quests = activeQuests(heroQuests ?? null);
  if (quests.length === 0) return null;
  return (
    <section className="flex flex-col gap-1.5" data-testid="hero-quest-section">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {t('heroes.quests')}
      </h3>
      {quests.map((quest) => (
        <QuestRow key={quest.id} quest={quest} />
      ))}
    </section>
  );
}

/** One order, on one hero. */
function QuestRow({ quest }: { readonly quest: HeroQuest }) {
  const t = useT();
  return (
    <div
      className="flex flex-col gap-0.5 rounded-md border border-border/50 bg-card/60 px-2.5 py-2"
      data-testid={`hero-quest-${quest.id}`}
    >
      <div className="flex items-center gap-2 text-xs">
        <span className="min-w-0 flex-1 truncate font-medium text-foreground">{quest.name}</span>
        <span className="shrink-0 text-[10px] text-muted-foreground">
          {quest.state === 'active'
            ? t('heroes.quest.progress', { made: quest.progress, needed: quest.amount })
            : t(`heroes.quest.${quest.state}`)}
        </span>
      </div>
      <span className="text-[10px] text-muted-foreground">
        {quest.kind === 'hunt'
          ? t('heroes.order.kind.hunt')
          : t('heroes.order.slay', { n: quest.amount })}
      </span>
      {quest.state === 'active' && (
        <span className="text-[10px] text-muted-foreground">
          {t('heroes.quest.due', { n: quest.deadline })}
        </span>
      )}
    </div>
  );
}
